import { atom, useAtomValue } from "jotai";
import { defaultServicesAtom, partCustomServicesAtom } from "./atom";
import { ServiceComponents, ServiceCustomData } from "./types";

const serviceComponentsAtom = atom<ServiceComponents>((get) => {
  const { components } = get(defaultServicesAtom);
  return components;
});

const lpPageUuidAtom = atom<string | undefined>((get) => {
  const { lpPageUuid } = get(defaultServicesAtom);
  return lpPageUuid;
});

/**
 * Hook to get default services (20 newest from API)
 * Used for system mode parts
 */
export const useServiceComponents = (): ServiceComponents =>
  useAtomValue(serviceComponentsAtom);

/**
 * Hook to get LP page UUID
 */
export const useLpPageUuid = (): string | undefined =>
  useAtomValue(lpPageUuidAtom);

/**
 * Hook to get custom services data for a specific part
 * Used for custom mode parts
 * @param partId - The part ID (block key)
 */
export const usePartCustomServices = (partId: string): ServiceCustomData => {
  const allCustomServices = useAtomValue(partCustomServicesAtom);
  return allCustomServices[partId] || { components: [] };
};

/**
 * Hook to get displayed services for a specific part
 * For customer: reads from template data + atoms (no separate state)
 *
 * @param partId - The part ID (block key)
 * @param mode - Display mode from template ("system" | "custom")
 * @returns Service components to display
 */
export const useDisplayedServices = (
  partId: string,
  mode?: "system" | "custom",
): ServiceComponents => {
  const defaultServices = useAtomValue(serviceComponentsAtom);
  const customServices = usePartCustomServices(partId);

  if (mode === "custom") {
    return customServices.components;
  }
  return defaultServices;
};
